import { PassedInitialConfig, LogLevel } from 'angular-auth-oidc-client';
import { environment } from '../../../environments/environment';

/**
 * Authorization Code + PKCE, straight to AuthServer. No gateway cookie, no YARP route for the SPA.
 *
 * Why bearer and not the shared .myAspenApp cookie: Mobile/ (Capacitor) cannot use a gateway cookie
 * and must use PKCE anyway, and PHASE3 item 3 plans to retire the cookie from Core.Api. Building on
 * the cookie would mean writing an auth flow we already intend to delete, then writing PKCE anyway.
 *
 * `authority` is the gateway's /auth route, because that is the issuer AuthServer stamps and exactly
 * what Core.Api validates. See environment.ts.
 *
 * offline_access + refresh tokens are REQUIRED, not just for silent renew: switching the active
 * client works by persisting the choice (POST /api/session/active-client) and then forcing a token
 * refresh, so AuthServer re-mints the ActiveClientId claim. The client must never assert that claim
 * itself, or the multi-client query filter could be bypassed.
 */
// Derived from the app's <base href>, NOT a hardcoded "/web". document.baseURI resolves the base
// href the Angular builder writes per configuration — "/" for the default (standalone) dev build so
// this is `https://localhost:{base}/`, and "/web/" for `dev-proxy` (angular.json baseHref) so it is
// `https://localhost:{base+2}/web/`. That exactly matches the redirect/post-logout URIs SeedData
// derives from Cors:SpaOrigins in each mode (standalone `{origin}/callback`, proxied
// `{origin}/web/callback`). Hardcoding "/web" sent `…:{base}/web/callback` in standalone, which is
// not a registered redirect URI, so /connect/authorize failed with 400 Bad Request.
const baseUri = document.baseURI; // always ends with "/"

/**
 * BYOD: where discovery is fetched from, when that is not `{authority}/.well-known/...`.
 *
 * Only the multi-hostname build sets `authWellknownEndpointUrl` (see environment.dev-remote.ts).
 * When it does, the library's RFC 8414 check has to be relaxed: it asserts that the retrieved
 * `metadata.issuer` equals the configured well-known URL minus its suffix, and here they
 * deliberately differ — metadata comes from the customer's hostname while the issuer stays the one
 * canonical value AuthServer stamps.
 *
 * The check is switched off ONLY on that build, never globally, and what it protects is not lost:
 *   - the ID token's `iss` is still validated against `metadata.issuer` (a separate check,
 *     `validateIdTokenIss`, which this does not touch);
 *   - signatures are still verified against the JWKS named in that metadata;
 *   - the URL is built from `window.location.origin`, i.e. an origin the gateway is already bound
 *     to and serving this bundle from — not attacker-supplied input.
 */
const wellKnownOverride = environment.authWellknownEndpointUrl;

export const authConfig: PassedInitialConfig = {
  config: {
    authority: environment.authority,
    ...(wellKnownOverride
      ? {
          authWellknownEndpointUrl: wellKnownOverride,
          strictIssuerValidationOnWellKnownRetrievalOff: true,
        }
      : {}),
    clientId: environment.clientId,
    redirectUrl: `${baseUri}callback`,
    // Trailing slash is load-bearing: OpenIddict compares post_logout_redirect_uri to the registered
    // value as an ordinal string, and SeedData registers `{origin}{appPath}/` (Uri normalises the
    // empty path to "/"). document.baseURI already carries that trailing slash.
    postLogoutRedirectUri: baseUri,
    scope: 'openid profile email roles offline_access',
    responseType: 'code',

    // PKCE is implied by responseType 'code' in this library; the OpenIddict client is registered
    // with ProofKeyForCodeExchange as a hard requirement, so a non-PKCE attempt is rejected.
    silentRenew: true,
    useRefreshToken: true,
    // OpenIddict rotates refresh tokens by default; the library must store each new one.
    allowUnsafeReuseRefreshToken: false,
    renewTimeBeforeTokenExpiresInSeconds: 60,

    // The access token is held in memory only. Refresh tokens live in session storage (the library's
    // default store) rather than localStorage, so they do not outlive the browser session.
    ignoreNonceAfterRefresh: true,

    // Debug in dev: it logs whether each outgoing request matched a secureRoute and got a bearer,
    // which is the difference between "no token sent" and "token sent but rejected".
    logLevel: environment.production ? LogLevel.Error : LogLevel.Debug,

    // Attach the bearer token only to our own API. Anything else (CDNs, third parties) must never
    // receive it.
    secureRoutes: [environment.apiBaseUrl],
  },
};
